import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "count"];
import "core-js/modules/es.array.map.js";
import React from 'react';
import { NavLink, Switch, Route, Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { breakpoints, spacing } from 'styles/globals';
import config from 'utils/config';
import { checkIsHelixUser } from 'utils/helix/checkIsHelixUser';
import { useFetchUserProfile } from 'actions/userProfile';
import { useFetchMyContentStats } from 'actions/content';
import Box from 'components/Box';
import PageSection from 'components/PageSection';
import ScrollableNav from 'components/ScrollableNav';
import NavBarTab from 'components/Tab/NavBarTab';
import PageTitle from 'components/PageTitle';
import Text from 'components/Text';
import Landmark from 'components/Landmark';
import UserHeader from './UserHeader';
import UserProgress from './UserProgress';
import Saved from './saved';
import History from './history';
import Todo from './to-do';
import Badges from './badges';
import UserSkillsTab from './skills';
var PROFILE_ROUTES = {
  root: '/profile',
  badges: '/profile/badges',
  todo: '/profile/to-do',
  saved: '/profile/saved',
  history: '/profile/history',
  skills: '/profile/skills'
};
function getFirstTabToDisplay(_ref) {
  var hasSkillsTab = _ref.hasSkillsTab,
    hasTodoTab = _ref.hasTodoTab,
    hasSavedItems = _ref.hasSavedItems;
  if (hasSkillsTab) {
    return PROFILE_ROUTES.skills;
  } else if (hasTodoTab) {
    return PROFILE_ROUTES.todo;
  } else if (hasSavedItems) {
    return PROFILE_ROUTES.saved;
  } else {
    return PROFILE_ROUTES.history;
  }
}
var LEGACY_PROFILE_ROUTES = ['/profile/playlists', '/profile/assets', '/profile/favourites'];
var HeaderGrid = styled.div.withConfig({
  displayName: "profile__HeaderGrid",
  componentId: "sc-exeeq-0"
})(["display:flex;align-items:center;padding-top:", ";@media (max-width:", "){flex-direction:column;}"], spacing.m, breakpoints.xs);
var HeaderGridMain = styled.div.withConfig({
  displayName: "profile__HeaderGridMain",
  componentId: "sc-exeeq-1"
})(["flex-grow:1;flex-shrink:1;@media (max-width:", "){margin-bottom:", ";}"], breakpoints.xs, spacing.m);
function ProfileTab(_ref2) {
  var children = _ref2.children,
    count = _ref2.count,
    otherProps = _objectWithoutProperties(_ref2, _excluded);
  return /*#__PURE__*/React.createElement(NavBarTab, _extends({
    as: NavLink,
    activeClassName: "is-active",
    height: 60,
    stackBelow: "xxs"
  }, otherProps), children, Boolean(count) &&
  /*#__PURE__*/
  // NOTE: <Pill /> is not being used here as it does not resize on mobile view
  React.createElement(React.Fragment, null, ' ', /*#__PURE__*/React.createElement(Box, {
    borderRadius: "circular",
    background: "shaded",
    display: "inline-block"
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    px: "xs"
  }, count))));
}
function ProfilePage() {
  var _userProfile$learning, _userProfile$learning2, _userProfile$learning3, _userProfile$learning4, _userProfile$userTeam;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useFetchUserProfile = useFetchUserProfile(),
    userProfile = _useFetchUserProfile.userProfile,
    isUserProfileLoading = _useFetchUserProfile.isLoading;
  var _useFetchMyContentSta = useFetchMyContentStats(),
    items = _useFetchMyContentSta.items,
    isContentStatsLoading = _useFetchMyContentSta.isLoading;
  var isLoading = isUserProfileLoading || isContentStatsLoading || !userProfile || !items;
  if (isLoading) {
    return /*#__PURE__*/React.createElement(Landmark, {
      icon: "spinner"
    });
  }
  var savedCount = items.savedCount,
    badgesTotalCount = items.badgesTotalCount,
    badgesAwardedCount = items.badgesAwardedCount,
    dueCountIncomplete = items.dueCountIncomplete,
    dueCountComplete = items.dueCountComplete;
  var badges = config.badges;
  var assetsCount = (_userProfile$learning = userProfile === null || userProfile === void 0 ? void 0 : (_userProfile$learning2 = userProfile.learning_progress) === null || _userProfile$learning2 === void 0 ? void 0 : _userProfile$learning2.totalCompletedAssets) !== null && _userProfile$learning !== void 0 ? _userProfile$learning : 0;
  var totalMinutes = (_userProfile$learning3 = userProfile === null || userProfile === void 0 ? void 0 : (_userProfile$learning4 = userProfile.learning_progress) === null || _userProfile$learning4 === void 0 ? void 0 : _userProfile$learning4.totalLearningTime) !== null && _userProfile$learning3 !== void 0 ? _userProfile$learning3 : 0;
  var badgesLockedCount = badgesTotalCount - badgesAwardedCount;
  var hasBadgeTab = badges && badgesTotalCount > 0;
  var hasTodoTab = dueCountIncomplete + dueCountComplete > 0;
  var teams = (_userProfile$userTeam = userProfile.userTeams) === null || _userProfile$userTeam === void 0 ? void 0 : _userProfile$userTeam.map(function (userTeam) {
    return userTeam.team;
  });
  var hasSkillsTab = checkIsHelixUser(teams);
  var firstTabToDisplay = getFirstTabToDisplay({
    hasSkillsTab: hasSkillsTab,
    hasTodoTab: hasTodoTab,
    hasSavedItems: savedCount > 0
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PageTitle, {
    visuallyHidden: true
  }, t('navbar_profile')), /*#__PURE__*/React.createElement(PageSection, null, /*#__PURE__*/React.createElement(HeaderGrid, null, /*#__PURE__*/React.createElement(HeaderGridMain, null, /*#__PURE__*/React.createElement(UserHeader, {
    profile: userProfile
  })), /*#__PURE__*/React.createElement(UserProgress, {
    totalMinutes: totalMinutes,
    assetCount: assetsCount
  }))), /*#__PURE__*/React.createElement(PageSection, null, /*#__PURE__*/React.createElement(Box, {
    borderBottom: "thin",
    px: "xxs"
  }, /*#__PURE__*/React.createElement(ScrollableNav, {
    spacing: "l"
  }, hasSkillsTab && /*#__PURE__*/React.createElement(ProfileTab, {
    to: PROFILE_ROUTES.skills,
    icon: "trending"
  }, t('your_skills_title')), hasTodoTab && /*#__PURE__*/React.createElement(ProfileTab, {
    to: PROFILE_ROUTES.todo,
    icon: "calendar",
    count: dueCountIncomplete
  }, t('to_do_title')), /*#__PURE__*/React.createElement(ProfileTab, {
    to: PROFILE_ROUTES.saved,
    icon: "favourite",
    count: savedCount
  }, t('saved_for_later_title')), hasBadgeTab && /*#__PURE__*/React.createElement(ProfileTab, {
    to: PROFILE_ROUTES.badges,
    icon: "star",
    count: badgesAwardedCount
  }, t('badges_title')), /*#__PURE__*/React.createElement(ProfileTab, {
    to: PROFILE_ROUTES.history,
    icon: "undo"
  }, t('history_title'))))), /*#__PURE__*/React.createElement(Switch, null, LEGACY_PROFILE_ROUTES.map(function (route) {
    return /*#__PURE__*/React.createElement(Redirect, {
      key: route,
      exact: true,
      from: route,
      to: firstTabToDisplay
    });
  }), /*#__PURE__*/React.createElement(Redirect, {
    exact: true,
    from: PROFILE_ROUTES.root,
    to: firstTabToDisplay
  }), hasSkillsTab && /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: PROFILE_ROUTES.skills,
    component: UserSkillsTab
  }), hasTodoTab && /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: PROFILE_ROUTES.todo,
    component: Todo
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: PROFILE_ROUTES.saved,
    component: Saved
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: PROFILE_ROUTES.history,
    component: History
  }), hasBadgeTab && /*#__PURE__*/React.createElement(Route, {
    path: PROFILE_ROUTES.badges,
    render: function render() {
      return /*#__PURE__*/React.createElement(Badges, {
        badgesLockedCount: badgesLockedCount,
        badgesAwardedCount: badgesAwardedCount
      });
    }
  })));
}
export default ProfilePage;